/**
 * suggestType — heuristic classifier for the QuickAdd flow.
 *
 * Runs entirely in the browser, zero LLM cost. Used to pre-select a
 * type chip the moment the user pauses typing, so the path collapses
 * from 3 clicks to 2 (open → save) when the AI guess is correct.
 *
 * Signals (highest confidence first):
 *   1. URL anywhere in text → reading
 *   2. Currency / amount tokens → payment
 *   3. "call / ring / phone X" verbs → call
 *   4. Date + time tokens ("3pm", "at 4", "tomorrow at") → event
 *   5. "deliver to / promised / owe X" stakeholder verbs → commitment
 *   6. "note / remember / fyi / log that" reflective verbs → note
 *   7. fallback → task
 *
 * Returns an object `{ type, hints }` where hints carries the parsed
 * sub-fields (url, amount, contactName, deadline) so the form can
 * pre-fill them without another regex round-trip.
 */

const URL_RE = /\bhttps?:\/\/\S+|\bwww\.[^\s]+/i;
const AMOUNT_RE = /(?:[₹$€£¥]\s?\d|(?:\b\d[\d,]*(?:\.\d{1,2})?\b\s*(?:USD|INR|EUR|GBP|JPY|rs|rupees?|dollars?)))/i;
// Payment-verb signal — catches bare-number phrases like "pay 500 to
// landlord" or "send 250 to vendor" that AMOUNT_RE misses because they
// have no currency symbol/word. Must come BEFORE the call regex (since
// "pay" sometimes co-occurs with "to <Name>" which would otherwise trip
// the call extractor).
const PAYMENT_VERB_RE = /\b(?:pay(?:ment)?|paid|invoice|bill|send|wire|transfer)\s+\d/i;
const CALL_RE = /\b(?:call|ring|phone|dial|reach out to)\s+([A-Z][\w' -]{1,40}|[a-z][\w' -]{2,40})/i;
const TIME_RE = /\b(?:\d{1,2}(?::\d{2})?\s?(?:am|pm)|\d{1,2}:\d{2}\b)/i;
const RELATIVE_DAY_RE = /\b(?:today|tomorrow|tonight|next\s+(?:mon|tue|wed|thu|fri|sat|sun))/i;
const COMMITMENT_RE = /\b(?:deliver(?:ed)? to|promised|owe[ds]?|committed to|will (?:send|deliver|pay|share))\b/i;
const NOTE_RE = /^\s*(?:note(?:\s*:|\s+to\s+self)|remember|fyi|log that|jot down)\b/i;
const CONTACT_CAPTURE_RE = /\b(?:to|for|from|with)\s+([A-Z][\w' -]{1,40})/;

function extractUrl(text) {
  const m = text.match(URL_RE);
  return m ? m[0] : '';
}

function extractAmount(text) {
  const m = text.match(/(\d[\d,]*(?:\.\d{1,2})?)/);
  if (!m) return '';
  const cleaned = m[1].replace(/,/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : '';
}

function extractContactName(text) {
  const m = text.match(CALL_RE) || text.match(CONTACT_CAPTURE_RE);
  return m ? m[1].trim() : '';
}

export function suggestType(rawText) {
  const text = (rawText || '').trim();
  if (!text) return { type: null, hints: {} };

  // 1. URL → reading
  if (URL_RE.test(text)) {
    return { type: 'reading', hints: { url: extractUrl(text) } };
  }

  // 2. Currency / amount → payment (currency-token path).
  if (AMOUNT_RE.test(text)) {
    return { type: 'payment', hints: { amount: extractAmount(text) } };
  }

  // 2b. Payment-verb + bare number → payment (catches "pay 500 to X",
  // "send 250 to vendor", etc. — no currency symbol required).
  if (PAYMENT_VERB_RE.test(text)) {
    return { type: 'payment', hints: { amount: extractAmount(text) } };
  }

  // 3. Note verbs (check before call, since "note: call John" should
  //    land as a note, not a call entity).
  if (NOTE_RE.test(text)) {
    return { type: 'note', hints: {} };
  }

  // 4. Call verbs
  if (CALL_RE.test(text)) {
    return { type: 'call', hints: { contactName: extractContactName(text) } };
  }

  // 5. Event signals — time + (relative day OR "at")
  if (TIME_RE.test(text) && (RELATIVE_DAY_RE.test(text) || /\bat\b/i.test(text))) {
    return { type: 'event', hints: {} };
  }

  // 6. Commitment verbs
  if (COMMITMENT_RE.test(text)) {
    return { type: 'commitment', hints: { stakeholder: extractContactName(text) } };
  }

  // 7. Fallback
  return { type: 'task', hints: {} };
}
